'use client'

import React, { FunctionComponent, useEffect, useState } from 'react'
import Link from 'next/link'
import Image from '@elements/Image'
import { useTheme } from 'next-themes'
import { motion, AnimatePresence } from 'framer-motion'

import GitHub from '@svgs/github.svg'
import StackOverFlow from '@svgs/stackoverflow.svg'
import LinkedIn from '@svgs/linkedin.svg'
import Envelope from '@svgs/envelope.svg'
import LightIcon from '@svgs/light.svg'
import DarkIcon from '@svgs/dark.svg'

interface NavigationProps {}

const Navigation: FunctionComponent<NavigationProps> = () => {
  const { theme, setTheme, resolvedTheme } = useTheme()
  const [mounted, setMounted] = useState(false)

  // useEffect only runs on the client, so now we can safely show the UI
  useEffect(() => {
    setMounted(true)
  }, [])

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark')
  }

  const navItemVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0 },
  }

  return (
    <motion.nav
      initial="hidden"
      animate="visible"
      transition={{ duration: 0.5, staggerChildren: 0.1 }}
      className="fixed top-0 left-0 h-auto w-full z-50 flex justify-center pointer-events-none"
    >
      <div className="max-w-[calc(72rem+2rem)] w-full rounded-[2rem] m-4 px-4 py-2 bg-white/10 dark:bg-black/20 backdrop-blur-md border border-white/20 shadow-lg flex pointer-events-auto">
        <motion.div variants={navItemVariants} className="flex items-center">
          <Image
            src="/logo.svg"
            alt="Vu Thanh Nguyen's logo"
            width="2rem"
            height="2rem"
            sizes="(max-width: 768px) 240px, 400px"
            priority
          />
        </motion.div>
        <div className="flex gap-2 ml-auto my-auto">
          {[
            {
              href: 'https://stackoverflow.com/users/9201587/nick-vu',
              label: 'StackOverFlow',
              Icon: StackOverFlow,
            },
            {
              href: 'https://github.com/vuthanhnguyen92',
              label: 'GitHub',
              Icon: GitHub,
            },
            {
              href: 'https://www.linkedin.com/in/vuthanhnguyen92/',
              label: 'LinkedIn',
              Icon: LinkedIn,
            },
            {
              href: 'mailto:vuthanhnguyen92@gmail.com',
              label: 'Email',
              Icon: Envelope,
            },
          ].map((item) => (
            <motion.div
              key={item.label}
              variants={navItemVariants}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link href={item.href} aria-label={item.label} target="_blank">
                <div className="w-10 h-10 p-2 flex items-center justify-center rounded-full cursor-pointer transition-colors duration-300 text-[var(--foreground)] hover:bg-[var(--foreground)] hover:text-[var(--background)] active:bg-[var(--foreground)] active:text-[var(--background)]">
                  <item.Icon className="w-full h-full" />
                </div>
              </Link>
            </motion.div>
          ))}
          <motion.button
            variants={navItemVariants}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="w-10 h-10 p-2 flex items-center justify-center rounded-full cursor-pointer transition-colors duration-300 text-[var(--foreground)] hover:bg-[var(--foreground)] hover:text-[var(--background)] active:bg-[var(--foreground)] active:text-[var(--background)]"
          >
            {mounted ? (
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={resolvedTheme}
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 20, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {resolvedTheme === 'dark' ? (
                    <LightIcon className="w-full h-full" />
                  ) : (
                    <DarkIcon className="w-full h-full" />
                  )}
                </motion.div>
              </AnimatePresence>
            ) : (
              <div className="w-full h-full" />
            )}
          </motion.button>
        </div>
      </div>
    </motion.nav>
  )
}

export default Navigation
